import React, { useEffect, useState, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useTeam } from 'shared/context/teamContext';
import useBillingV2Status from 'shared/hooks/useBillingV2Status';
import BrandLoader from 'shared/components/ui/BrandLoader';
import AskSpendCard from 'features/settings/components/panels/AskSpendCard';
import {
  Section, Stat, DailyBars, btnNeutral, tableShell, th, thRow, trBody, td, MUTED,
} from 'features/settings/components/panels/askCreditsShared';

const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';
const TRACK = 'var(--border-subtle)';
const FILL = 'var(--trodo-primary)';
const num = (n) => Number(n || 0).toLocaleString();

function isFiniteLimit(limit) {
  if (limit === null || limit === undefined) return false;
  const n = Number(limit);
  return Number.isFinite(n) && n > 0;
}

/** Evals, agents, alerts and datasets: where this period's Executions went (TRO-493). Datasets are credits: 1 per 1,000 rows of each source run. */
function ExecutionsBreakdown({ teamId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!teamId) return undefined;
    let live = true;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    axios.get(`${API_URL}/api/billing/v2/executions`, { params: { teamId }, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [teamId]);
  const bars = useMemo(() => (data?.daily || []).map((d) => ({
    key: d.day,
    label: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: (d.evals || 0) + (d.agents || 0) + (d.alerts || 0) + (d.datasets || 0),
  })), [data]);
  if (!data) return null;
  return (
    <Section title="Executions by source">
      <div className="mb-5 flex flex-wrap items-end gap-10">
        <Stat label="Evals" value={num(data.evals)} />
        <Stat label="Agents" value={num(data.agents)} />
        <Stat label="Alerts" value={num(data.alerts)} />
        <Stat label="Datasets" value={num(data.datasets)} />
        {data.uncounted > 0 ? <Stat label="Past the limit, not charged" value={num(data.uncounted)} /> : null}
        {data.unmetered > 0 ? <Stat label="No plan in force, not counted" value={num(data.unmetered)} /> : null}
      </div>
      {bars.length > 1 ? <DailyBars data={bars} format={(v) => `${num(v)} executions`} /> : null}
    </Section>
  );
}

function formatResetCountdown(isoDate) {
  if (!isoDate) return null;
  const end = new Date(isoDate);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 'Resets soon';
  const totalMin = Math.floor(ms / (1000 * 60));
  const d = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  if (d >= 2) return `Resets in ${d} days`;
  if (d === 1) return 'Resets in 1 day';
  if (h >= 1) return `Resets in ${h} hr ${m} min`;
  if (m >= 1) return `Resets in ${m} min`;
  return 'Resets soon';
}

const UsageDashboard = ({ usageData, planData, subscriptionData, onRefresh }) => {
  const { selectedTeam } = useTeam();
  const [refreshing, setRefreshing] = useState(false);

  // The entitlement and its counters — the numbers the gate and the invoice
  // are built from. Legacy `usageData` cannot answer these.
  const { status: v2 } = useBillingV2Status(selectedTeam?.id ?? selectedTeam?._id);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setRefreshing(false);
    }
  };

  const getDaysUntilReset = (resetDate) => {
    if (!resetDate) return null;
    try {
      const reset = new Date(resetDate);
      if (isNaN(reset.getTime())) return null;
      const diffDays = Math.ceil((reset - new Date()) / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (e) {
      return null;
    }
  };

  const getUsagePeriod = () => {
    // The counters' own period wins. The rows above are read from that period,
    // so a header derived from anything else can disagree with them — it was
    // showing "June 1 – June 30" over August's numbers.
    const counterPeriod = v2?.usage?.periodStart;
    if (counterPeriod) {
      const start = new Date(counterPeriod);
      if (!Number.isNaN(start.getTime())) {
        const end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        end.setDate(end.getDate() - 1);
        const f = (d) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${f(start)} – ${f(end)}`;
      }
    }
    const isFreePlan = !subscriptionData?.plan || subscriptionData?.plan === 'free';
    const billingCycle = subscriptionData?.billingCycle || 'monthly';
    if (isFreePlan) {
      const createdAt = selectedTeam?.created_at || selectedTeam?.createdAt;
      if (createdAt) {
        const date = new Date(createdAt);
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return `${start.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })} – ${end.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    } else if (billingCycle === 'annual') {
      const periodStart = subscriptionData?.currentPeriodStart;
      const periodEnd = subscriptionData?.currentPeriodEnd;
      const now = new Date();
      if (periodStart && periodEnd) {
        const billingStart = new Date(periodStart);
        const billingEnd = new Date(periodEnd);
        const dayOfMonth = billingStart.getDate();
        const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
        if (now < currentPeriodStart) currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
        const nextPeriodStart = new Date(currentPeriodStart);
        nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
        nextPeriodStart.setDate(nextPeriodStart.getDate() - 1);
        const actualPeriodEnd = nextPeriodStart > billingEnd ? billingEnd : nextPeriodStart;
        const actualPeriodStart = currentPeriodStart < billingStart ? billingStart : currentPeriodStart;
        return `${actualPeriodStart.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} – ${actualPeriodEnd.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    } else {
      const periodStart = subscriptionData?.currentPeriodStart;
      const periodEnd = subscriptionData?.currentPeriodEnd;
      if (periodStart && periodEnd) {
        const start = new Date(periodStart);
        const end = new Date(periodEnd);
        return `${start.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    }
    return 'Current period';
  };

  const getCurrentPeriodEnd = () => {
    if (subscriptionData?.billingCycle !== 'annual') return null;
    const periodStart = subscriptionData?.currentPeriodStart;
    const periodEnd = subscriptionData?.currentPeriodEnd;
    const now = new Date();
    if (!periodStart || !periodEnd) return null;
    const billingStart = new Date(periodStart);
    const billingEnd = new Date(periodEnd);
    const dayOfMonth = billingStart.getDate();
    const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
    if (now < currentPeriodStart) currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
    const nextPeriodStart = new Date(currentPeriodStart);
    nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
    nextPeriodStart.setDate(nextPeriodStart.getDate() - 1);
    return nextPeriodStart > billingEnd ? billingEnd : nextPeriodStart;
  };

  const currentPeriodEnd = getCurrentPeriodEnd();
  const creditResetDate =
    (subscriptionData?.billingCycle === 'annual' && currentPeriodEnd && currentPeriodEnd.toISOString()) ||
    usageData?.nextResetAt ||
    (usageData?.billingAnchorAt
      ? (() => {
          const anchor = new Date(usageData.billingAnchorAt);
          anchor.setMonth(anchor.getMonth() + 1);
          return anchor.toISOString();
        })()
      : null) ||
    (usageData?.lastResetAt
      ? (() => {
          const lastReset = new Date(usageData.lastResetAt);
          lastReset.setMonth(lastReset.getMonth() + 1);
          return lastReset.toISOString();
        })()
      : null) ||
    (subscriptionData?.currentPeriodEnd ? new Date(subscriptionData.currentPeriodEnd).toISOString() : null);

  const daysUntilReset = getDaysUntilReset(creditResetDate);
  const resetSubtitleFinite = useMemo(() => {
    const fromCountdown = formatResetCountdown(creditResetDate);
    // "Resets soon" is what the old countdown fell back to whenever it could
    // not work out a date, which for a team with no Stripe period was always.
    // Saying nothing beats saying something wrong about when a limit lifts.
    if (fromCountdown && fromCountdown !== 'Resets soon') return fromCountdown;
    // > 0 only. A stale legacy period made this compute zero and render
    // "Resets in 0 days", which reads as "any moment now" on an allowance
    // that actually has weeks left.
    if (daysUntilReset != null && daysUntilReset > 0) {
      return `Resets in ${daysUntilReset} ${daysUntilReset === 1 ? 'day' : 'days'}`;
    }
    return null;
  }, [creditResetDate, daysUntilReset]);

  const ent = v2?.entitlement || null;
  const v2Usage = v2?.usage || {};
  // The entitlement's consumables are what the gate blocks on and what an
  // overage invoice is built from.
  const rows = ent
    ? [
      { key: 'units', label: 'Traces', used: v2Usage.units ?? 0, limit: Number(ent.units_included), overNote: v2?.overage?.armed ? 'billed' : null },
      { key: 'eval_scores', label: 'Executions', used: v2?.executions?.used ?? v2Usage.eval_scores ?? 0, limit: Number(ent.eval_scores_included), overNote: v2?.executions?.metering ? 'billed' : null },
    ]
    : [];

  const teamId = selectedTeam?.id ?? selectedTeam?._id;

  return (
    <div className="w-full text-left" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px]" style={{ color: MUTED }}>{getUsagePeriod()}</p>
        <button type="button" onClick={handleRefresh} disabled={refreshing} className={btnNeutral}>
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} strokeWidth={2} />
          Refresh
        </button>
      </div>

      <Section title="Included this period">
        {!v2 ? (
          <BrandLoader size="md" label="Loading usage" />
        ) : !rows.length ? (
          <div className="text-[12px]" style={{ color: MUTED }}>No plan limits to show.</div>
        ) : (
          <div className={tableShell}>
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className={thRow}>
                  <th className={th}>Metric</th>
                  <th className={`${th} text-right`}>Used</th>
                  <th className={`${th} text-right`}>Included</th>
                  <th className={`${th} text-right`}>Left</th>
                  <th className={th} style={{ width: '32%' }}>Usage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const finite = isFiniteLimit(r.limit);
                  const over = finite ? Math.max(0, r.used - r.limit) : 0;
                  const pct = finite ? Math.min(100, (r.used / r.limit) * 100) : 0;
                  return (
                    <tr key={r.key} className={trBody} style={{ height: 40 }}>
                      <td className={td}>{r.label}</td>
                      <td className={`${td} text-right tabular-nums`}>{num(r.used)}</td>
                      <td className={`${td} text-right tabular-nums`}>{finite ? num(r.limit) : 'Unlimited'}</td>
                      <td className={`${td} text-right tabular-nums`} style={over > 0 ? { color: 'var(--feedback-warning)' } : undefined}>
                        {!finite ? '' : over > 0 ? `${num(over)} over${r.overNote ? `, ${r.overNote}` : ''}` : num(r.limit - r.used)}
                      </td>
                      <td className={td}>
                        {finite ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="h-1.5 flex-1 overflow-hidden rounded-full"
                              style={{ backgroundColor: TRACK }}
                              role="progressbar"
                              aria-valuenow={Math.round(pct)}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-label={`${r.label} usage`}
                            >
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: over > 0 ? 'var(--feedback-warning)' : FILL }} />
                            </div>
                            <span className="w-9 text-right text-[11px] tabular-nums" style={{ color: MUTED }}>{Math.round((r.used / r.limit) * 100)}%</span>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {resetSubtitleFinite ? (
          <p className="mt-2 text-[11px]" style={{ color: MUTED }}>{resetSubtitleFinite}</p>
        ) : null}
      </Section>

      {ent ? <ExecutionsBreakdown teamId={teamId} /> : null}

      <AskSpendCard />
    </div>
  );
};

export default UsageDashboard;
